import { useState, memo, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { CircleNotch } from '@phosphor-icons/react'

const MeshGradient = memo(function MeshGradient() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Blue blob */}
      <motion.div
        animate={{ x: [0, 60, -30, 0], y: [0, -40, 20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[10%] left-[30%] w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(37, 99, 235, 0.3) 0%, transparent 70%)' }}
      />
      {/* Indigo blob */}
      <motion.div
        animate={{ x: [0, -40, 50, 0], y: [0, 60, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[30%] left-[10%] w-[450px] h-[450px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(79, 70, 229, 0.25) 0%, transparent 70%)' }}
      />
      {/* Cyan blob */}
      <motion.div
        animate={{ x: [0, -50, 30, 0], y: [0, -60, 30, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[45%] left-[40%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)' }}
      />
    </div>
  )
})

export function LoginPage() {
  const { session, signIn, loading, sessionExpired } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-bg flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
        >
          <CircleNotch size={28} weight="light" className="text-accent" />
        </motion.div>
      </div>
    )
  }

  if (session) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    const { error } = await signIn(email, password)
    if (error) {
      setError(error)
    }
    setSubmitting(false)
  }

  return (
    <div className="min-h-[100dvh] bg-bg grid lg:grid-cols-[2fr_1fr]">
      {/* Left — branding panel */}
      <div className="hidden lg:flex flex-col justify-between relative overflow-hidden p-12 pb-16">
        <MeshGradient />

        {/* Top — logo area (replace with your logo) */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 25 }}
          className="relative z-10 flex items-center gap-2.5"
        >
          <span className="text-2xl font-semibold tracking-tight text-text-primary">
            TIC Comms
          </span>
        </motion.div>

        {/* Bottom — headline */}
        <div className="relative z-10 max-w-[560px]">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 100, damping: 20 }}
            className="text-6xl md:text-7xl font-semibold tracking-tighter text-text-primary leading-[1.02] mb-6"
          >
            Shared inbox{' '}
            <span className="text-text-muted font-light">for your team.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, type: 'spring', stiffness: 100, damping: 20 }}
            className="text-lg text-text-secondary leading-relaxed max-w-[50ch] mb-10"
          >
            Manage conversations, collaborate with your team, and keep everything organised in one place.
          </motion.p>
        </div>
      </div>

      {/* Right — form panel */}
      <div className="flex items-center justify-center p-8 lg:p-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 25 }}
          className="w-full max-w-[380px] lg:bg-white lg:rounded-[2rem] lg:shadow-[0_16px_48px_rgba(0,0,0,0.12)] lg:border lg:border-border/30 lg:p-10"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-12">
            <span className="text-2xl font-semibold tracking-tight text-text-primary">
              TIC Comms
            </span>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-semibold tracking-tight text-text-primary">
              Sign in
            </h1>
            <p className="text-base text-text-secondary mt-2">
              Enter your credentials to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {sessionExpired && !error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring' as const, stiffness: 300, damping: 25 }}
                className="bg-accent/5 border border-accent/10 rounded-xl px-4 py-3 text-sm text-text-secondary"
              >
                Your session has expired. Please sign in again.
              </motion.div>
            )}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="bg-danger/5 border border-danger/10 rounded-xl px-4 py-3 text-sm text-danger"
              >
                {error}
              </motion.div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />

            <div className="pt-2">
              <Button type="submit" className="w-full" size="lg" loading={submitting}>
                Sign in
              </Button>
            </div>
          </form>

          <div className="mt-10 pt-5 border-t border-border/50">
            <p className="text-sm text-text-secondary">
              Internal use only. Contact an admin for access.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
